import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendar, 
  faClock, 
  faExchangeAlt, 
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faChevronDown,
  faChevronUp,
  faTicket
} from '@fortawesome/free-solid-svg-icons';
import config from '../../../config/environment';

const Card = styled.div`
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    border-color: #192f6a;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const BookingRef = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #192f6a;
  font-family: monospace;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TransferredBadge = styled.span`
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
`;

const CurrentDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  
  strong {
    color: #333;
  }
`;

const TransferButton = styled.button`
  background: linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 47, 106, 0.3);
  }
`;

const TransferSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid #e9ecef;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #192f6a;
    box-shadow: 0 0 0 3px rgba(25, 47, 106, 0.1);
  }
`;

const EventsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const EventCard = styled.div`
  padding: 1rem;
  border: 2px solid ${props => props.selected ? '#192f6a' : '#dee2e6'};
  border-radius: 8px;
  background: ${props => props.selected ? '#e7f3ff' : 'white'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #192f6a;
    background: #f8f9fa;
  }
`;

const EventDate = styled.div`
  font-weight: 700;
  color: #192f6a;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EventTime = styled.div`
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Capacity = styled.div`
  font-size: 0.9rem;
  color: ${props => {
    if (props.full) return '#dc3545';
    if (props.low) return '#ffc107';
    return '#28a745';
  }};
  font-weight: 600;
`;

const ErrorBox = styled.div`
  background: #f8d7da;
  border: 2px solid #dc3545;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SuccessBox = styled.div`
  background: #d4edda;
  border: 2px solid #28a745;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BookingCard = ({ 
  booking, 
  transactionRef, 
  sessionToken, 
  onTransferSuccess, 
  suggestedDate = '', 
  isTransferred = false,
  totalBookings = 1
}) => {
  const [showTransfer, setShowTransfer] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bookingDetails, setBookingDetails] = useState(null);
  
  // Auto-populate suggested date when opening transfer section
  useEffect(() => {
    if (showTransfer && suggestedDate && !selectedDate && !isTransferred) {
      setSelectedDate(suggestedDate);
      // Automatically load events for suggested date
      handleDateChange(suggestedDate);
    }
  }, [showTransfer, suggestedDate, isTransferred]);

  // Use booking details from props if available, otherwise fetch
  useEffect(() => {
    if (booking.details) {
      setBookingDetails(booking.details);
    } else {
      fetchBookingDetails();
    }
  }, [booking.booking_reference, booking.details]);

  const fetchBookingDetails = async () => {
    try {
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      
      const url = `${apiBase}/venpos-booking-details.php?booking_reference=${encodeURIComponent(booking.booking_reference)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success && data.booking) {
        setBookingDetails(data.booking);
      }
    } catch (err) {
      console.error('Error fetching booking details:', err);
    }
  };

  const handleShowTransfer = () => {
    setShowTransfer(!showTransfer);
    setError('');
    setSuccess('');
    setSelectedEvent(null);
    setAvailableEvents([]);
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    setSelectedEvent(null);
    setError('');
    
    if (!date) {
      setAvailableEvents([]);
      return;
    }

    setLoading(true);
    try {
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      
      // Get event template ID from booking details
      const eventTemplateId = bookingDetails?.event?.event_template_id || bookingDetails?.event?.template_id || null;
      
      // Convert date to ISO format for the API
      const selectedDate = new Date(date + 'T00:00:00');
      const fromDate = selectedDate.toISOString();
      
      // Build query params (same as afternoon tea booking page)
      const params = new URLSearchParams({
        from: fromDate,
        page: '1',
        pageSize: '100'
      });
      
      // Add event template ID if available
      if (eventTemplateId) {
        params.append('event_template_id', eventTemplateId);
      }
      
      const url = `${apiBase}/venpos-available-events.php?${params.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success && data.events) {
        setAvailableEvents(data.events.Items || []);
      } else {
        setError(data.error || 'Failed to load available events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedEvent) {
      setError('Please select a new date and time for your booking');
      return;
    }

    // Validate minimum 24-hour notice
    if (bookingDetails && bookingDetails.event && bookingDetails.event.start) {
      const bookingDateTime = new Date(bookingDetails.event.start);
      const now = new Date();
      const hoursUntilBooking = (bookingDateTime - now) / (1000 * 60 * 60);
      
      if (hoursUntilBooking < 24) {
        setError('Bookings can only be transferred at least 24 hours before the scheduled date and time.');
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.apiEndpoint}/custom/v1/customer-transfer-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: sessionToken,
          booking_reference: booking.booking_reference,
          event_id: selectedEvent.Id,
          reason: 'Customer requested transfer via self-service portal'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Booking transferred successfully! A confirmation email has been sent to you.');
        const transferredDate = selectedDate; // Store before clearing
        setShowTransfer(false);
        setSelectedEvent(null);
        setSelectedDate('');
        setAvailableEvents([]);
        if (onTransferSuccess) {
          onTransferSuccess(booking.booking_reference, transferredDate);
        }
      } else {
        setError(data.message || data.error?.message || 'Failed to transfer booking. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return dateString;
    }
  };

  const currentDate = bookingDetails?.event?.start 
    ? new Date(bookingDetails.event.start).toISOString().split('T')[0]
    : null;
  const currentTime = bookingDetails?.event?.start 
    ? formatTime(bookingDetails.event.start)
    : 'N/A';

  // Calculate minimum date (24 hours from now)
  const minDate = new Date();
  minDate.setHours(minDate.getHours() + 24);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <Card>
      <CardHeader>
        <BookingRef>
          {booking.booking_reference}
          {isTransferred && (
            <TransferredBadge>
              <FontAwesomeIcon icon={faCheckCircle} />
              Transferred
            </TransferredBadge>
          )}
        </BookingRef>
        <TransferButton onClick={handleShowTransfer} disabled={loading || isTransferred}>
          <FontAwesomeIcon icon={showTransfer ? faChevronUp : faChevronDown} />
          {isTransferred ? 'Already Transferred' : (showTransfer ? 'Hide Transfer' : 'Transfer Booking')}
        </TransferButton>
      </CardHeader>

      <CurrentDetails>
        <DetailItem style={{ gridColumn: '1 / -1', fontSize: '1.05rem' }}>
          <FontAwesomeIcon icon={faTicket} style={{ color: '#192f6a' }} />
          <div>
            <strong>Event:</strong><br />
            {bookingDetails?.event?.description || 'Loading...'}
          </div>
        </DetailItem>
        <DetailItem>
          <FontAwesomeIcon icon={faCalendar} />
          <div>
            <strong>Current Date:</strong><br />
            {currentDate ? formatDate(bookingDetails?.event?.start) : 'Loading...'}
          </div>
        </DetailItem>
        <DetailItem>
          <FontAwesomeIcon icon={faClock} />
          <div>
            <strong>Current Time:</strong><br />
            {currentTime}
          </div>
        </DetailItem>
      </CurrentDetails>

      {error && (
        <ErrorBox>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </ErrorBox>
      )}

      {success && (
        <SuccessBox>
          <FontAwesomeIcon icon={faCheckCircle} />
          {success}
        </SuccessBox>
      )}

      {showTransfer && (
        <TransferSection>
          <h3 style={{ marginBottom: '1rem', color: '#192f6a' }}>
            <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '0.5rem' }} />
            Transfer to New Date & Time
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Select New Date (minimum 24 hours notice required)
            </label>
            <DateInput
              type="date"
              min={minDateStr}
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
            />
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#192f6a' }} />
              <p style={{ marginTop: '1rem', color: '#666' }}>Loading available times...</p>
            </div>
          )}

          {!loading && selectedDate && availableEvents.length === 0 && (
            <div style={{ padding: '1rem', background: '#fff3cd', borderRadius: '8px', color: '#856404' }}>
              No available events found for this date. Please try a different date.
            </div>
          )}

          {!loading && availableEvents.length > 0 && (
            <>
              <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Available Times:</p>
              <EventsList>
                {availableEvents.map((event) => {
                  const eventDate = new Date(event.Start);
                  const capacity = event.Capacity - event.Usage - event.Reserved;
                  const isFull = capacity <= 0;
                  const isLow = capacity < 5 && capacity > 0;
                  const isSelected = selectedEvent && selectedEvent.Id === event.Id;

                  return (
                    <EventCard
                      key={event.Id}
                      selected={isSelected}
                      onClick={() => !isFull && setSelectedEvent(event)}
                    >
                      <EventDate>
                        <FontAwesomeIcon icon={faCalendar} />
                        {formatDate(event.Start)}
                      </EventDate>
                      <EventTime>
                        <FontAwesomeIcon icon={faClock} />
                        {formatTime(event.Start)} - {formatTime(event.End)}
                      </EventTime>
                      <Capacity full={isFull} low={isLow}>
                        {isFull ? 'Fully Booked' : `${capacity} spaces available`}
                      </Capacity>
                      {isSelected && (
                        <div style={{ marginTop: '0.5rem', color: '#28a745', fontWeight: 600 }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                          Selected
                        </div>
                      )}
                    </EventCard>
                  );
                })}
              </EventsList>

              {selectedEvent && (
                <TransferButton
                  onClick={handleTransfer}
                  disabled={loading}
                  style={{ marginTop: '1rem', width: '100%' }}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Transferring...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      Confirm Transfer
                    </>
                  )}
                </TransferButton>
              )}
            </>
          )}
        </TransferSection>
      )}
    </Card>
  );
};

export default BookingCard;
